import React from 'react';
import { Editor, DiffEditor, EditorProps } from '@monaco-editor/react';
import { useCopilotReadable } from '@copilotkit/react-core';

import { useColorScheme } from 'hooks/useColorScheme';
import { useAccount } from 'hooks/api/useAccount';
import { useModal } from 'hooks/useModal';
import { useCopilot } from 'context/copilot/CopilotProvider';
import { Button } from 'components/Button/Button';

import { Assistant } from 'pipelines/components/CodeEditor/Assistant';
import {
  CodeEditorProvider,
  useCodeEditorContext,
} from 'pipelines/components/CodeEditor/CodeEditorContext';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';

type CodeEditorProps = EditorProps & {
  disabled?: boolean;
  nodeID?: string;
};

// Copilot calls need to be in a separate element to only render it when copilot is available,
// otherwise copilot hooks will crash.
function CopilotCodeEditorContext({
  nodeID,
  code,
}: {
  nodeID: string;
  code: string;
}) {
  // Expose custom function node code to Copilot
  useCopilotReadable({
    description: 'Currently open custom function node code',
    value: {
      nodeID,
      code,
    },
  });

  return null;
}

export function CodeEditor({
  value,
  onChange,
  disabled: readOnly,
  nodeID,
}: CodeEditorProps) {
  const handleAccept = React.useCallback(
    (code: string) => {
      onChange?.(code, undefined as any);
    },
    [onChange]
  );

  return (
    <CodeEditorProvider
      originalCode={value ?? ''}
      nodeID={nodeID ?? null}
      onAccept={handleAccept}
    >
      <CodeEditorInner
        value={value}
        onChange={onChange}
        readOnly={readOnly}
        nodeID={nodeID}
      />
    </CodeEditorProvider>
  );
}

type CodeEditorInnerProps = {
  value: string | undefined;
  onChange: EditorProps['onChange'];
  readOnly?: boolean;
  nodeID?: string;
};

function CodeEditorInner({
  value,
  onChange,
  readOnly,
  nodeID,
}: CodeEditorInnerProps) {
  const colorScheme = useColorScheme();
  const {
    isDiffMode,
    proposedCode,
    acceptProposal,
    rejectProposal,
    originalCode,
  } = useCodeEditorContext();

  const { isAvailable: isCopilotAvailable } = useCopilot();
  const { isLumeoDomainAccount } = useAccount();
  const { open } = useModal();

  const [isAssistantOpen, setIsAssistantOpen] = React.useState(false);

  // Check if the global AskAIButton would be visible
  const isGlobalAskAIVisible = isCopilotAvailable && isLumeoDomainAccount;

  const theme = colorScheme === 'dark' ? 'vs-dark' : 'light';

  return (
    <div
      className={`grid ${isAssistantOpen ? 'grid-cols-2 gap-2' : 'grid-cols-1'}`}
    >
      <div className="relative">
        {isDiffMode && proposedCode !== null ? (
          <div className="relative h-[50vh] pb-12">
            <DiffEditor
              className="rounded-md border border-gray-200 dark:border-none"
              language="python"
              original={originalCode}
              modified={proposedCode}
              width="100%"
              height="100%"
              theme={theme}
              options={{
                fontSize: 13,
                scrollBeyondLastLine: false,
                minimap: { enabled: false },
                readOnly: true,
                renderSideBySide: false,
                renderOverviewRuler: false,
                diffWordWrap: 'on',
              }}
            />
            <DiffActions onAccept={acceptProposal} onReject={rejectProposal} />
          </div>
        ) : (
          <>
            {isCopilotAvailable && nodeID && value && (
              <CopilotCodeEditorContext nodeID={nodeID} code={value} />
            )}
            <Editor
              className="rounded-md border border-gray-200 dark:border-none"
              language="python"
              value={value}
              onChange={onChange}
              width="100%"
              height="50vh"
              theme={theme}
              options={{
                fontSize: 13,
                tabSize: 2,
                stickyScroll: { enabled: false },
                scrollBeyondLastLine: false,
                minimap: { enabled: !isAssistantOpen },
                readOnly,
              }}
            />
            {!isAssistantOpen && (
              <div className="absolute right-8 bottom-4 z-10">
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => {
                    if (isGlobalAskAIVisible) {
                      open('copilot-chat');
                    } else {
                      setIsAssistantOpen(true);
                    }
                  }}
                >
                  <Icon name="logo" size="small" />
                  <span>Ask AI</span>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      {isAssistantOpen && (
        <div className="relative h-[50vh]">
          <div className="absolute top-4 right-4 z-10">
            <IconButton
              icon="cancel"
              label="Close assistant"
              size="small"
              onClick={() => setIsAssistantOpen(false)}
            />
          </div>
          <Assistant functionNodeCode={value} />
        </div>
      )}
    </div>
  );
}

type DiffActionsProps = {
  onAccept: () => void;
  onReject: () => void;
};

function DiffActions({ onAccept, onReject }: DiffActionsProps) {
  return (
    <div className="absolute right-4 bottom-4 z-10 flex gap-2">
      <Button variant="secondary" size="small" onClick={onReject}>
        <Icon name="cancel" size="small" />
        <span>Reject</span>
      </Button>
      <Button variant="primary" size="small" onClick={onAccept}>
        <Icon name="check" size="small" />
        <span>Accept</span>
      </Button>
    </div>
  );
}
